/**
 * Where an image was uploaded from, and which campaign brought the visit that uploaded it
 * (#4558). Stored on `images.uploadSource` / `uploadPage` / `uploadAttribution` so uploads —
 * and the one that tips a member into Tier 2 — can be split by surface and by `utm_source`.
 *
 * - uploadSource       the surface that ran the upload, from UPLOAD_SOURCES
 * - uploadPage         origin + route template of the page the browser was on, plus its
 *                      utm_* / click-ID params only (`sanitizeUploadPage`); never a slug,
 *                      id, token, fragment or other query param
 * - uploadAttribution  utm_* and ad click IDs from THIS tab session (`getSessionAttribution`)
 *
 * Framework-free: client and town-square build the fields with `uploadOrigin`; the API
 * re-validates every one with `normalizeUploadOrigin`, because the browser is untrusted.
 */
import { getSessionAttribution, sanitizeAttribution, type RawAttribution } from './attribution';
import { SUPPORTED_LOCALES } from './i18n';

export const UPLOAD_SOURCES = [
  // town-square
  'profile_gallery', // owner's profile page: Add artwork dialog, `?upload=1`
  'arty_upload_card', // the ShowUpload card Arty renders in a chat
  'arty_sparklink_modal', // spark link "upload your art" modal
  'arty_attachment', // paperclip attachment in an Arty chat composer (both apps)
  'guild_artwork_picker', // guild "add artwork" picker
  'artist_standard_band', // artist-standard page's add-artwork band
  'community_artwork_intake', // Upload tab of a community post's artwork intake modal
  // client
  'my_artwork', // My Artwork library (ImageManager + dashboard)
  'add_artwork_page', // /add-artwork
  'add_artwork_modal', // Add artwork modal opened from a notification
  'chat_upload_tool', // upload card a client chat tool renders
  'onboarding', // welcome / onboarding upload steps
  // both
  'direct_message', // photo attached to a DM — not artwork
  // server-side
  'guest_chat', // attachments carried over when a signed-out chat is claimed
  'profile_claim', // images migrated in when an unclaimed profile is claimed
  // a value this API build does not recognise (a newer client than the API)
  'unknown',
] as const;

export type UploadSource = (typeof UPLOAD_SOURCES)[number];

const UPLOAD_SOURCE_SET: ReadonlySet<string> = new Set(UPLOAD_SOURCES);

// Stamped by the API itself; a browser sending one is stored as 'unknown'.
const SERVER_ONLY_SOURCES: ReadonlySet<string> = new Set<UploadSource>(['guest_chat', 'profile_claim']);

const MAX_PAGE_LENGTH = 512;
const MAX_URL_LENGTH = 2048;
// Only a placeholder is stored for a segment that is not here, so a slug, id or link
// credential never reaches the column. A route missing from this list is stored as
// `:param` — less precise, never a leak. Static segments of the town-square app router
// and the client's routes.
const STATIC_ROUTE_SEGMENTS: ReadonlySet<string> = new Set<string>([
  ...SUPPORTED_LOCALES,
  'about',
  'activity',
  'add',
  'admin',
  'all',
  'applications',
  'art',
  'art-marketing-autopilot',
  'artist-genome',
  'artist-spotlight',
  'artist-standard',
  'artist-statement',
  'artists',
  'assistant',
  'awards',
  'badges',
  'billing',
  'blocked',
  'bookmarks',
  'bounties',
  'calendar',
  'callback',
  'chat',
  'chats',
  'claim',
  'community',
  'community-invites',
  'community-standards',
  'compare',
  'connections',
  'contact',
  'contacts',
  'contests',
  'critiques',
  'dashboard',
  'discover',
  'edit',
  'editor',
  'email',
  'features',
  'feed-cards',
  'gallery',
  'genome',
  'guilds',
  'hashtag',
  'home',
  'human-artist-index',
  'image',
  'integrations',
  'invite',
  'join',
  'leaderboard',
  'location',
  'medium',
  'messages',
  'my-artwork',
  'network',
  'new',
  'notifications',
  'onboarding',
  'plans',
  'points',
  'polls',
  'post',
  'pricing',
  'profile',
  'profiles',
  'quests',
  'ratings',
  'referrals',
  'requests',
  'rewards',
  'room',
  'saved',
  'search',
  'settings',
  'shared',
  'signin',
  'signup',
  'sparklinks',
  'sparks',
  'steps',
  'submit',
  'tools',
  'tutorials',
  'upload',
  'users',
  'verified',
  'verify',
  'videos',
  'welcome',
]);
// The segment after one of these is a link credential (e.g. `/bounties/invite/[token]`),
// so it is `:token` even when it happens to spell a static route word.
const TOKEN_ROUTE_SEGMENTS: ReadonlySet<string> = new Set([
  'invite',
  'join',
  'share',
  'shared',
  'claim',
  'verify',
  'redeem',
  'accept',
  'magic',
  'r',
]);
// Placeholder base for parsing a relative page; never stored.
const RELATIVE_BASE = 'http://relative.invalid';

export interface UploadOrigin {
  uploadSource: UploadSource;
  uploadPage: string | null;
  uploadAttribution: RawAttribution | null;
}

export function isUploadSource(value: unknown): value is UploadSource {
  return typeof value === 'string' && UPLOAD_SOURCE_SET.has(value);
}

export interface UploadPageOptions {
  /** First-party origins. A page on any other origin is dropped; omit to accept any http(s) origin. */
  allowedOrigins?: readonly string[];
}

/**
 * An untrusted page reduced to origin + route template + attribution params:
 * `https://host/en/profile/:param?utm_source=x`. Every path segment that is not a static
 * route word becomes a placeholder, and every query param that is not utm_* or a known
 * click ID is dropped, as are the fragment and any userinfo. A relative path (`/en/profile`)
 * is kept relative. Null for anything else, or for an origin outside `allowedOrigins`.
 */
export function sanitizeUploadPage(value: unknown, options: UploadPageOptions = {}): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_URL_LENGTH) return null;
  const relative = value.startsWith('/') && !value.startsWith('//');
  let url: URL;
  try {
    url = relative ? new URL(value, RELATIVE_BASE) : new URL(value);
  } catch {
    return null;
  }
  if (!relative) {
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (options.allowedOrigins && !isAllowedOrigin(url, options.allowedOrigins)) return null;
  }

  const path = `${relative ? '' : url.origin}${routeTemplate(url.pathname)}`;
  if (path.length > MAX_PAGE_LENGTH) return null;
  const query = attributionQuery(url.searchParams);
  const page = query ? `${path}?${query}` : path;
  return page.length > MAX_PAGE_LENGTH ? path : page;
}

function routeTemplate(pathname: string): string {
  const segments = pathname.split('/');

  return segments
    .map((segment, index) => {
      if (!segment) return segment;
      if (index > 0 && TOKEN_ROUTE_SEGMENTS.has(segments[index - 1]!.toLowerCase())) return ':token';
      return STATIC_ROUTE_SEGMENTS.has(segment) ? segment : ':param';
    })
    .join('/');
}

function attributionQuery(params: URLSearchParams): string {
  const first = new Map<string, string>();
  for (const [key, value] of params) {
    if (!first.has(key)) first.set(key, value);
  }
  return new URLSearchParams(sanitizeAttribution(Object.fromEntries(first))).toString();
}

// `www.` is ignored so an apex/www mismatch between a stage's env and its live host
// does not drop every page.
function originKey(url: URL): string {
  return `${url.protocol}//${url.host.replace(/^www\./, '')}`;
}

function isAllowedOrigin(url: URL, allowedOrigins: readonly string[]): boolean {
  const key = originKey(url);
  return allowedOrigins.some((allowed) => {
    try {
      return originKey(new URL(allowed)) === key;
    } catch {
      return false;
    }
  });
}

function attributionOrNull(input: unknown): RawAttribution | null {
  const clean = sanitizeAttribution(input as Record<string, unknown> | null | undefined);
  if (Object.keys(clean).length === 0) return null;
  return clean;
}

/** The fields a browser upload sends with `createImage`. */
export function uploadOrigin(source: UploadSource): UploadOrigin {
  if (typeof window === 'undefined') {
    return { uploadSource: source, uploadPage: null, uploadAttribution: null };
  }

  return {
    uploadSource: source,
    uploadPage: sanitizeUploadPage(window.location.href),
    uploadAttribution: attributionOrNull(getSessionAttribution()),
  };
}

/**
 * Server side of `uploadOrigin`. A missing source stays null (an old client); a source this
 * build does not know is kept as `unknown` rather than refused — failing an upload over an
 * analytics field would cost the member their artwork. So is a server-only source
 * (`guest_chat`, `profile_claim`): only the API's own create paths may stamp those.
 */
export function normalizeUploadOrigin(
  input: {
    uploadSource?: unknown;
    uploadPage?: unknown;
    uploadAttribution?: unknown;
  },
  options: UploadPageOptions = {}
): { uploadSource: UploadSource | null; uploadPage: string | null; uploadAttribution: RawAttribution | null } {
  return {
    uploadSource: normalizeSource(input.uploadSource),
    uploadPage: sanitizeUploadPage(input.uploadPage, options),
    uploadAttribution: attributionOrNull(input.uploadAttribution),
  };
}

function normalizeSource(value: unknown): UploadSource | null {
  if (value === undefined || value === null || value === '') return null;
  if (isUploadSource(value) && !SERVER_ONLY_SOURCES.has(value)) return value;
  return 'unknown';
}
